import { API } from '@/modules/api'
import { defineStore } from 'pinia'
import type { UserData } from '@/modules/api/get/userData.ts'
import { useSessionStore } from '@/stores/session'
import type { LoggedInResponse } from '@/modules/api/get/loggedIn.ts'
import type { AutoPayTermsStatusResponse } from '@/modules/api/post/autoPayTermsStatus.ts'
import type {
  AutoPaymentSettingsResponse,
  AutoPaymentDayRange
} from '@/modules/api/get/autoPaymentSettings.ts'
import { useStudentFeeSimpleStore } from '@/stores/studentFeeSimple'
import { useCorePowerschoolFeeStore } from '@/stores/corePowerschoolFees'
import { useDestinyFineStore } from '@/stores/destinyFine'
import { useIncidentIqStore } from '@/stores/incidentIq'
import { useMbaCafeLunchStore } from '@/stores/mbaCafeLunch'
import { useMbaPowerschoolFeeStore } from '@/stores/mbaPowerschoolFees'
import { useSkywardFeeStore } from '@/stores/skywardFee'
import { useSkywardLunchStore } from '@/stores/skywardLunch'
import { useWordwareLunchStore } from '@/stores/wordwareLunch'
import { useTuitionContractStore } from '@/stores/tuitionContract'
import { useAffinetyActivityStore } from '@/stores/affinetyActivities'
import { useAffinetyActivityFeesStore } from '@/stores/affinetyActivityFees'
import { useFamilyUserInvoiceStore } from '@/stores/familyUserInvoice'
import { useEPayTrakFeesStore } from '@/stores/ePayTrakFees'
import { useInvoiceStore } from '@/stores/invoice'
import { useCartStore } from '@/stores/cart'
import type { AthleticsCatalog, Enrollment } from '@/types/enrollments'

const MODE_ENV = import.meta.env.MODE_ENV
const LOBBY_URL = import.meta.env.LOBBY_URL

export const useStore = defineStore('store', {
  state: () => ({
    localeCode: 'en',
    isLoaded: false,
    gotCSRFToken: false,
    loginCheckedLast: null as null | number,
    familyUserId: null as null | number,
    familyUserData: null as null | UserData,
    sessionExpiresAt: null as null | number,
    districts: [] as TDistrict[],
    families: [] as TFamily[],
    students: [] as TStudent[],
    fundingSources: {} as Record<TDistrictId, TFundingSource[]>,
    autoPayTermsStatus: null as null | AutoPayTermsStatusResponse,
    ssoPayload: null as string | null,
    ssoResponse: {} as TSSOResponse,
    schools: [] as TSchool[],
    enrollments: {} as Enrollment[],
    autoPaymentSettings: {} as Record<TDistrictId, AutoPaymentSettingsResponse | null>,
    // District-wide allowed day-of-month range, populated alongside
    // autoPaymentSettings. Absent districts default to {min:1, max:31}.
    autoPaymentDayRanges: {} as Record<TDistrictId, AutoPaymentDayRange>,
    // Season order/labels + sport icons for the athletics Register view
    // (DTA-799). Fetched once per session, on first use.
    athleticsCatalog: null as null | AthleticsCatalog
  }),

  getters: {
    getFamily(state) {
      return (familyAccountId: number) => {
        const family = state.families.find(family => family.familyAccountId === familyAccountId)
        if (!family) throw new Error(`Data for family #${familyAccountId} not found`)
        return family
      }
    },
    getStudent(state) {
      return (studentAccountId: number) => {
        const student = state.students.find(
          student => student.studentAccountId === studentAccountId
        )
        if (!student) throw new Error(`Data for student #${studentAccountId} not found`)
        return student
      }
    },
    getDistrict(state) {
      return (districtId: number) => {
        const district = state.districts.find(district => district.districtId === districtId)
        if (!district) throw new Error(`Data for district #${districtId} not found`)
        return district
      }
    },
    // Release-gate read for a district id. Unlike `getDistrict` this NEVER
    // throws: a district that is absent from `checkout_data`, a district object
    // that predates the field, and an unknown gate name all resolve to OFF.
    isReleaseGateActive(state) {
      return (districtId: number, gateName: string) => {
        const district = state.districts.find(district => district.districtId === districtId)
        return district?.releaseGates?.[gateName] === true
      }
    },
    // DTA-949: is athletics (Sports & Activities, fundraisers, sponsors, coupon
    // books) on for this district? Strict — an absent district or an absent
    // `athleticsEnabled` field is OFF. Never throws.
    athleticsOn(state) {
      return (districtId: number | string | null | undefined) => {
        const id = Number(districtId)
        const district = state.districts.find(district => district.districtId === id)
        return district?.athleticsEnabled === true
      }
    },
    // DTA-949: true when at least one of the family's districts has athletics on.
    anyAthleticsOn(state) {
      return state.districts.some(district => district.athleticsEnabled === true)
    },
    // DTA-1092: is the new Childcare app on for this district? Strict — an absent
    // district or an absent `childcareEnabled` field is OFF. Never throws.
    childcareOn(state) {
      return (districtId: number | string | null | undefined) => {
        const id = Number(districtId)
        const district = state.districts.find(district => district.districtId === id)
        return district?.childcareEnabled === true
      }
    },
    // DTA-1092: true when at least one of the family's districts has Childcare on.
    anyChildcareOn(state) {
      return state.districts.some(district => district.childcareEnabled === true)
    },
    getStudentsInDistrict(state) {
      return (districtId: number) => {
        return state.students.filter(
          student => this.getFamily(student.familyAccountId).districtId === districtId
        )
      }
    },
    getStudentsInFamily(state) {
      return (familyAccountId: number) => {
        return state.students.filter(student => student.familyAccountId === familyAccountId)
      }
    },
    getFundingSources(state) {
      return (districtId: number) => {
        return state.fundingSources[districtId] ?? []
      }
    },
    getAllSchools(state) {
      return state.schools
    },
    getAllFamilies(state) {
      return state.families
    },
    getAllEnrollments(state) {
      return state.enrollments
    },
    getAllFamiliesInDistrict(state) {
      return (districtId: number) => {
        return state.families.filter(family => family.districtId === districtId)
      }
    },
    getStudentEnrollments(state) {
      return (studentAccountId: number) => {
        return state.enrollments.filter(
          enrollment => enrollment.studentAccountId === studentAccountId
        )
      }
    },
    getAutoPaymentSettings(state) {
      return (districtId: number) => {
        return state.autoPaymentSettings[districtId]
      }
    },
    getAutoPaymentDayRange(state) {
      return (districtId: number): AutoPaymentDayRange => {
        return state.autoPaymentDayRanges[districtId] ?? { min: 1, max: 31 }
      }
    }
  },

  actions: {
    clearSession() {
      useSessionStore().clear()
      useCartStore().clear()
      this.familyUserData = null
      // Drop any Sanctum bearer token so it doesn't override a newly-authenticated session.
      localStorage.removeItem('auth_token')
    },
    processLoginCheckResponse(response: LoggedInResponse) {
      const sessionStore = useSessionStore()

      if (sessionStore.getFamilyUserId() !== null) {
        if (response.familyUserId === null) {
          console.debug('Logged out, clearing session')
          this.clearSession()
          // Redirect to external lobby URL when logged out
          MODE_ENV === 'production' ? (window.location.href = LOBBY_URL) : ''
          return
        }

        if (sessionStore.getFamilyUserId() !== response.familyUserId) {
          console.debug('Family user changed, clearing session')
          this.clearSession()
          // Redirect to external lobby URL when user changes
          MODE_ENV === 'production' ? (window.location.href = LOBBY_URL) : ''
          return
        }
      }

      if (
        response.sessionTimeoutInterval !== null &&
        response.sessionIdleAt !== null &&
        response.sessionExpiresAt !== null
      ) {
        sessionStore.setFamilyUserId(response.familyUserId)
        sessionStore.setLocaleCode(response.localeCode, false)
        sessionStore.setTimeoutInterval(response.sessionTimeoutInterval)
        sessionStore.setLastRequestAt(response.sessionIdleAt)
        sessionStore.setSessionExpiresAt(response.sessionExpiresAt)
      }
    },
    async loggedIn(check = false, ssoPayload: string | null = null) {
      const sessionStore = useSessionStore()
      return new Promise<boolean>(async resolve => {
        if (!this.gotCSRFToken) {
          this.gotCSRFToken = true
          await API.Get.csrf()
        }

        if (
          (sessionStore.getFamilyUserId() !== null && sessionStore.getSessionTimedOut()) ||
          sessionStore.getSessionExpired()
        ) {
          // If we think session might be timed out or expired, check again to be certain
          console.debug(
            'Session timed out or expired and we are logged in, checking login status again'
          )
          check = true
        }
        try {
          if (ssoPayload && this.ssoPayload !== ssoPayload) {
            this.clearSession()
            this.ssoResponse = await API.Post.ssoLogin(ssoPayload)
            if (this.ssoResponse.success) {
              this.ssoPayload = ssoPayload
              check = true
            }
          }
        } catch (error) {
          this.ssoResponse.success = false
          this.ssoResponse.failureReason = error as string
        }

        //if (this.loginCheckedLast === null || check) { // commented out this line to avoid accidental login
        if (check) {
          const response = await API.Get.loggedIn()
          this.processLoginCheckResponse(response)
          this.loginCheckedLast = Date.now()
        }

        resolve(sessionStore.getFamilyUserId() !== null)
      })
    },

    async storeInitialize() {
      await this.fetchDashboardData()

      // Create an array of all store fetch promises
      const storeFetchPromises = []
      for (const district of this.districts) {
        useFamilyUserInvoiceStore().fetch(district.districtId)
        useEPayTrakFeesStore().fetch(district.districtId)
      }
      for (const student of this.students) {
        const family = this.getFamily(student.familyAccountId)
        const district = this.getDistrict(family.districtId)
        storeFetchPromises.push(
          useStudentFeeSimpleStore().fetch(district.districtId, student.studentAccountId)
        )
        if (district.hasWordwareLunch) {
          storeFetchPromises.push(
            useWordwareLunchStore().fetch(district.districtId, student.familyAccountId)
          )
        }
        if (district.hasSkywardLunch) {
          storeFetchPromises.push(
            useSkywardLunchStore().fetch(district.districtId, student.studentAccountId),
            useSkywardFeeStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        if (district.hasTuitionEnabled) {
          storeFetchPromises.push(
            useTuitionContractStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        if (district.hasPowerschoolMba) {
          storeFetchPromises.push(
            useMbaPowerschoolFeeStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        if (district.hasPowerschoolCore) {
          storeFetchPromises.push(
            useCorePowerschoolFeeStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        if (district.hasMbaCafeLunch) {
          storeFetchPromises.push(
            useMbaCafeLunchStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        if (district.hasActivityEnabled) {
          storeFetchPromises.push(
            useAffinetyActivityStore().fetch(district.districtId, student.studentAccountId),
            useAffinetyActivityFeesStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        if (district.hasDestinyFines) {
          storeFetchPromises.push(
            useDestinyFineStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        if (district.hasIncidentIq) {
          storeFetchPromises.push(
            useIncidentIqStore().fetch(district.districtId, student.studentAccountId)
          )
        }
        // storeFetchPromises.push(useProgramsStore().fetch(student.studentAccountId))
      }

      // Let the store fetches run in parallel without awaiting them
      Promise.all(storeFetchPromises).catch(error => {
        console.error('Error fetching store data:', error)
      })
    },

    async fetchDistrictStoreData(districtId: number) {
      const storeFetchPromises = []
      const district = this.getDistrict(districtId)
      storeFetchPromises.push(this.fetchAutoPaymentSettings(districtId))
      storeFetchPromises.push(useFamilyUserInvoiceStore().fetch(districtId))
      storeFetchPromises.push(
        this.fetchEPayTrakFees().then(() => useEPayTrakFeesStore().fetch(districtId))
      )
      storeFetchPromises.push(this.fetchStudentEnrollments())
      for (const student of this.students) {
        const family = this.getFamily(student.familyAccountId)
        if (family.districtId === districtId) {
          storeFetchPromises.push(
            useStudentFeeSimpleStore().fetch(districtId, student.studentAccountId)
          )
          if (district.hasWordwareLunch) {
            storeFetchPromises.push(
              useWordwareLunchStore().fetch(district.districtId, student.familyAccountId)
            )
          }
          if (district.hasSkywardLunch) {
            storeFetchPromises.push(
              useSkywardLunchStore().fetch(district.districtId, student.studentAccountId),
              useSkywardFeeStore().fetch(district.districtId, student.studentAccountId)
            )
          }
          if (district.hasTuitionEnabled) {
            storeFetchPromises.push(
              useTuitionContractStore().fetch(district.districtId, student.studentAccountId)
            )
          }
          if (district.hasPowerschoolMba) {
            storeFetchPromises.push(
              useMbaPowerschoolFeeStore().fetch(district.districtId, student.studentAccountId)
            )
          }
          if (district.hasPowerschoolCore) {
            storeFetchPromises.push(
              useCorePowerschoolFeeStore().fetch(district.districtId, student.studentAccountId)
            )
          }
          if (district.hasMbaCafeLunch) {
            storeFetchPromises.push(
              useMbaCafeLunchStore().fetch(district.districtId, student.studentAccountId)
            )
          }
          if (district.hasActivityEnabled) {
            storeFetchPromises.push(
              useAffinetyActivityStore().fetch(district.districtId, student.studentAccountId),
              useAffinetyActivityFeesStore().fetch(district.districtId, student.studentAccountId)
            )
          }
          if (district.hasDestinyFines) {
            storeFetchPromises.push(
              useDestinyFineStore().fetch(district.districtId, student.studentAccountId)
            )
          }
          if (district.hasIncidentIq) {
            storeFetchPromises.push(
              useIncidentIqStore().fetch(district.districtId, student.studentAccountId)
            )
          }
        }
      }
      Promise.all(storeFetchPromises).catch(error => {
        console.error('Error fetching store data:', error)
      })
    },
    async userData(check = false) {
      if (check || this.familyUserData === null) {
        this.familyUserData = await API.Get.userData()
      }

      return this.familyUserData
    },
    saveFundingSources(fundingSources: TFundingSource[], districtId: number) {
      this.fundingSources[districtId] = fundingSources
    },
    removeFundingSource(fundingSourceId: number, districtId: number) {
      if (!this.fundingSources[districtId]) {
        return
      }
      this.fundingSources[districtId] = this.fundingSources[districtId].filter(
        line => line.fundingSourceId !== fundingSourceId
      )
    },
    async fetchAutoPayInfo() {
      this.autoPayTermsStatus = await API.Post.autoPayTermsStatus()
    },
    async getAutoPayTermsAccepted(forceFetch: boolean = false) {
      if (!this.autoPayTermsStatus === null || forceFetch) {
        await this.fetchAutoPayInfo()
      }
      return this.autoPayTermsStatus !== null && this.autoPayTermsStatus.signatureRequired
    },
    async getAutoPayTermsText(forceFetch: boolean) {
      if (!this.autoPayTermsStatus === null || forceFetch) {
        await this.fetchAutoPayInfo()
      }
      return this.autoPayTermsStatus === null ? null : this.autoPayTermsStatus.latestTerms
    },
    async fetchDashboardData() {
      console.log('fetching dashboard data')
      this.$reset()
      const data = await API.Get.dashboardInfo()
      const schoolMap = new Map<string, string>()
      for (const district of data.checkoutData) {
        this.fetchAutoPaymentSettings(district.districtId)
        const { families, ...districtData } = district
        for (const family of families) {
          const { students, ...familyData } = family
          this.families.push({
            districtId: districtData.districtId,
            ...familyData
          })

          for (const student of students) {
            // Same guard as fetchStudents: a student the payload repeats
            // (two families, a re-entrant load) must not double every
            // listing and registration keyed off the students array.
            if (!this.students.some(s => s.studentAccountId === student.studentAccountId)) {
              this.students.push({
                familyAccountId: familyData.familyAccountId,
                ...student
              })
            }

            if (student.schoolId && student.schoolName) {
              schoolMap.set(student.schoolId.toString(), student.schoolName)
            }
          }
        }
        this.districts.push(districtData)
      }
      this.schools = Array.from(schoolMap.entries()).map(([schoolId, schoolName]) => ({
        schoolId,
        schoolName
      }))
      this.fundingSources = data.fundingSources
      await this.fetchStudentEnrollments()
      this.isLoaded = true
    },

    async fetchEPayTrakFees() {
      const data = await API.Get.dashboardInfo()
      for (const district of data.checkoutData) {
        const { families: _families, ...districtData } = district
        const existingIndex = this.districts.findIndex(
          d => d.districtId === districtData.districtId
        )
        if (existingIndex >= 0) {
          this.districts[existingIndex] = { ...this.districts[existingIndex], ...districtData }
        } else {
          this.districts.push(districtData)
        }
      }
    },

    async fetchAutoPaymentSettings(districtId: number) {
      const data = await API.Get.autoPaymentSettings(districtId)
      this.autoPaymentSettings[districtId] = data.settings
      this.autoPaymentDayRanges[districtId] = data.range
    },

    async fetchStudentEnrollments() {
      const allStudents = this.students
      const allStudentAccountIds = allStudents.map(student => student.studentAccountId)
      const allEnrollments: any[] = []

      for (const studentAccountId of allStudentAccountIds) {
        const enrollments = await API.Get.studentEnrollments(studentAccountId)
        allEnrollments.push(...enrollments)
      }

      this.enrollments = allEnrollments
    },

    async fetchStudents() {
      const data = await API.Get.dashboardInfo()
      for (const district of data.checkoutData) {
        const { families, ...districtData } = district
        for (const family of families) {
          const { students, ...familyData } = family
          for (const student of students) {
            if (!this.students.some(s => s.studentAccountId === student.studentAccountId)) {
              this.students.push({
                familyAccountId: familyData.familyAccountId,
                ...student
              })
            }
          }
        }
      }
    },

    async fetchAthleticsCatalog() {
      if (this.athleticsCatalog === null) {
        this.athleticsCatalog = await API.Get.athleticsCatalog()
      }
      return this.athleticsCatalog
    },

    async initializeDashboardData() {
      if (this.districts.length === 0) {
        await this.fetchDashboardData()
      }
    }
  }
})
